import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Search, Download } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';
import DownloadModal from '../Components/Download/DownloadModal';
import DownloadService from "../Components/Download/DownloadService";

// Custom Button Component
const Button = ({
  children,
  onClick,
  type = "button",
  variant = "primary",
  className = "",
}) => {
  const baseClasses = "px-4 py-2 rounded text-white font-medium shadow";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700",
    outline: "bg-transparent border border-blue-600 text-blue-600 hover:bg-blue-50",
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-green-600 hover:bg-green-700",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const AreaUnitManagement = () => {
  const { language, translations } = useLanguage();
  const [areaUnits, setAreaUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    unit_name: '',
    unit_code: '',
    conversion_to_sqm: '',
    remarks: '',
    isactive: 1
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all', 'active', 'inactive'

  // Download state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // Fetch all area units
  const fetchAreaUnits = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAreaUnits();
      setAreaUnits(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add new area unit
  const addAreaUnit = async () => {
    try {
      const response = await apiService.addAreaUnit(formData);
      setShowPopup(false);
      resetForm();
      fetchAreaUnits();
      alert("Area unit added successfully!");
    } catch (err) {
      setError(err.message);
      alert("Failed to add area unit: " + err.message);
    }
  };

  // Update area unit
  const updateAreaUnit = async () => {
    try {
      const response = await apiService.updateAreaUnit(editingId, formData);
      setShowPopup(false);
      resetForm();
      fetchAreaUnits();
      alert("Area unit updated successfully!");
    } catch (err) {
      setError(err.message);
      alert("Failed to update area unit: " + err.message);
    }
  };

  // Handle add button click
  const handleAddClick = () => {
    setIsEditing(false);
    setEditingId(null);
    resetForm();
    setShowPopup(true);
  };

  // Handle edit click
  const handleEdit = (unit) => {
    setIsEditing(true);
    setEditingId(unit.area_unit_id);
    setFormData({
      unit_name: unit.unit_name,
      unit_code: unit.unit_code,
      conversion_to_sqm: unit.conversion_to_sqm,
      remarks: unit.remarks,
      isactive: unit.isactive
    });
    setShowPopup(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      unit_name: '',
      unit_code: '',
      conversion_to_sqm: '',
      remarks: '',
      isactive: 1
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle popup close
  const handlePopupClose = () => {
    setShowPopup(false);
    resetForm();
  };

  // Handle form submit
  const handleSubmit = () => {
    if (isEditing) {
      updateAreaUnit();
    } else {
      addAreaUnit();
    }
  };

  // Toggle status
  const toggleStatus = (id) => {
    setAreaUnits((prev) =>
      prev.map((item) =>
        item.area_unit_id === id
          ? { ...item, isactive: !item.isactive }
          : item
      )
    );
  };

  // Define columns for download
  const downloadColumns = [
    { header: 'Unit Name', value: 'unit_name' },
    { header: 'Unit Code', value: 'unit_code' },
    { header: 'Conversion to SqM', value: 'conversion_to_sqm' },
    { header: 'Remarks', value: 'remarks' },
    // { header: 'Status', value: 'isactive' },
  ];

  const handleDownload = () => {
    if (filteredAreaUnits.length === 0) {
      alert('No data available to download');
      return;
    }
    setShowDownloadModal(true);
  };

  // Filter and search logic
  const filteredAreaUnits = areaUnits.filter((item) => {
    // Search term filter
    const matchesSearch =
      item.unit_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.unit_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.remarks && item.remarks.toLowerCase().includes(searchTerm.toLowerCase()));

    // Active status filter
    const matchesActive =
      activeFilter === "all" ||
      (activeFilter === "active" && item.isactive) ||
      (activeFilter === "inactive" && !item.isactive);

    return matchesSearch && matchesActive;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAreaUnits.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAreaUnits.length / itemsPerPage);

  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    fetchAreaUnits();
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-80">
          <div className="text-center animate-float">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Edit className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">{translations[language]?.loadingAreaUnits}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      {/* <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Area Unit Management</h1>
            <p className="text-gray-600">Manage area units and conversion rates</p>
          </div>
        </div>
      </div> */}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {translations[language]?.error}: {error}
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div className="relative w-full md:w-80 group">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              {translations[language]?.search}
            </label>
            <input
              type="text"
              placeholder={translations[language]?.Sau}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 placeholder-gray-400 
                focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                transition-all duration-200 shadow-sm group-hover:border-gray-300"
            />
            <div className="absolute left-3 top-10 text-gray-400 group-hover:text-gray-600 transition-colors duration-200">
              <Search className="h-5 w-5" />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-10 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Status</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div> */}

          {/* Download button */}
          <div className="mt-6">
            <button
              onClick={handleDownload}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              disabled={filteredAreaUnits.length === 0}
            >
              <Download className="mr-1 h-4 w-4" /> {translations[language]?.export}
            </button>
          </div>

          <div className="mt-8 md:mt-5">
            <button
              onClick={handleAddClick}
              className="px-5 py-3 text-sm bg-gradient-to-r from-blue-600 to-gray-900 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-blue-200 hover:shadow-blue-300 group"
            >
              <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
              <span className="font-medium">{translations[language]?.addNewAreaUnit}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-lg shadow border border-gray-200">
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead className="bg-blue-50">
            <tr>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.sNo}</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.unitName}</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.unitCode}</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.conversionToSqm}</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.remarks}</th>
              {/* <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th> */}
              <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{translations[language]?.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentItems.length > 0 ? (
              currentItems.map((unit, index) => (
                <tr key={unit.area_unit_id} className="hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium text-gray-700">{indexOfFirstItem + index + 1}</td>
                  <td className="py-2 px-4 font-medium text-gray-700">{unit.unit_name}</td>
                  <td className="py-2 px-4 text-gray-700">{unit.unit_code}</td>
                  <td className="py-2 px-4 text-gray-700">{unit.conversion_to_sqm}</td>
                  <td className="py-2 px-4 text-sm text-gray-500 max-w-xs truncate">{unit.remarks}</td>
                  {/* <td className="py-2 px-4 text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={unit.isactive}
                        onChange={() => toggleStatus(unit.area_unit_id)}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </td> */}
                  <td className="py-2 px-4 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(unit)}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-4 text-center text-gray-500">
                  {translations[language]?.noAreaUnitsFoundMatching}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredAreaUnits.length > itemsPerPage && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            {translations[language]?.showing} {indexOfFirstItem + 1} {translations[language]?.to}{" "}
            {Math.min(indexOfLastItem, filteredAreaUnits.length)} {translations[language]?.of}{" "}
            {filteredAreaUnits.length} {translations[language]?.entries}
          </div>
          <div className="flex space-x-1">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {translations[language]?.previous}
            </button>
            {[...Array(totalPages).keys()].map((number) => (
              <button
                key={number + 1}
                onClick={() => paginate(number + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === number + 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {number + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {translations[language]?.next}
            </button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            &#8203;
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {isEditing ? <span>{translations[language]?.EditAre}</span> : <span>{translations[language]?.AddArea}</span>}
                  </h3>
                  <button
                    onClick={handlePopupClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">{translations[language]?.close}</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {translations[language]?.unitName}<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="unit_name"
                        value={formData.unit_name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Decimal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {translations[language]?.unitCode} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="unit_code"
                        value={formData.unit_code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., DECIMAL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {translations[language]?.conversionToSqm} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="conversion_to_sqm"
                        value={formData.conversion_to_sqm}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 40.47"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{translations[language]?.remarks}</label>
                      <input
                        type="text"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 1 decimal = 1/100 acre = 40.47 sq m"
                      />
                    </div>
                  </div>

                  {/* Status Toggle - Only show in edit mode */}
                  {/* {isEditing && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isactive}
                          onChange={(e) => setFormData(prev => ({ ...prev, isactive: e.target.checked ? 1 : 0 }))}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-3 text-sm text-gray-600">
                          {formData.isactive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </div>
                  )} */}

                  <div className="flex gap-2 mt-6">
                    <Button
                      onClick={handleSubmit}
                      variant="primary"
                      className="flex items-center gap-2"
                    >
                      <Save size={16} />
                      {isEditing ? <span>{translations[language]?.update}</span> :<span>{translations[language]?.save}</span>}
                    </Button>
                    <Button
                      onClick={handlePopupClose}
                      variant="outline"
                      className="flex items-center gap-2 !text-gray-600 !border-gray-300"
                    >
                      <X size={16} />
                      {translations[language]?.cancel}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {areaUnits.length === 0 && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">{translations[language]?.noAreaUnitsFound}</p>
        </div>
      )}

      {/* Download Modal */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={translations[language]?.AreaUnitManagement}
        data={filteredAreaUnits}
        columns={downloadColumns}
        isLoading={isDownloadLoading}
        setIsLoading={setIsDownloadLoading}
        screenName="Area Unit Management"
      />
    </div>
  );
};

export default AreaUnitManagement;